import { Component, OnInit } from '@angular/core';
import { ProjectService } from '@app/services/project.service';
import { ProjectSlidesService } from '@app/services/project-slides.service';
import { ProjectCurrentLanguageService } from '@app/services/project-current-language.service';
import { LanguageRow, LanguageService } from '@app/services/language.service';
import {
  listSortIndicator,
  rowMatchesSearch,
  sortRows,
  toggleListSort,
} from '@app/utils/list-table-helpers';

import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';

type ProjectSortField =
  | 'ProjectShortCode'
  | 'ProjectName'
  | 'CustomerShortCode'
  | 'projectLifecycleStatus'
  | 'slidenos';

@Component({
  selector: 'app-project-list',
  templateUrl: './project-list.component.html',
  styleUrl: './project-list.component.scss'
})
export class ProjectListComponent implements OnInit {

  projectList: any[] = [];
  displayedProjectList: any[] = [];
  customers: any[] = [];
  languages: LanguageRow[] = [];
  error = '';
  loading = true;
  transferExportingCode: string | null = null;
  transferExportingMessage = '';
  customerSearch = '';
  sortField: ProjectSortField = 'ProjectShortCode';
  sortAsc = true;
  private readonly projectNumericSortFields = new Set<string>(['slidenos']);

  filterDtls = {
    status: 'A',
    customerCode: '',
    /** Empty = all; otherwise IN_PROGRESS | RELEASED | ARCHIVED */
    lifecycle: '' as '' | 'IN_PROGRESS' | 'RELEASED' | 'ARCHIVED',
  };

 
constructor(
    private projectService: ProjectService,
    private projectSlidesService: ProjectSlidesService,
    private projectCurrentLangSvc: ProjectCurrentLanguageService,
    private languageSvc: LanguageService,
    private toastr: ToastrService,
    private router: Router,
  ) {}
  ngOnInit(): void {
    this.loadProjects();
    this.loadLanguages();
    this.projectService.getCustomers().subscribe({
      next: (res: any) => {
        this.customers = Array.isArray(res) ? res : [];
      },
      error: () => {
        this.customers = [];
      },
    });
  }
  loadProjects() {
    const filters = [];

    if (this.filterDtls.status)
      filters.push({ fieldname: 'ProjectStatus', filtercriteria: 'equals', value: this.filterDtls.status });


    if (this.filterDtls.customerCode)
      filters.push({ fieldname: 'CustomerShortCode', filtercriteria: 'equals', value: this.filterDtls.customerCode });
    const lifecycle = this.filterDtls.lifecycle?.trim() || null;

    this.projectService.getActiveProjects(filters, lifecycle).subscribe({
      next: (res: any) => {
        console.log(res);
        this.projectList = Array.isArray(res) ? res : [];
        this.applyFilterAndSort();
        this.loading = false;
      },
      error: () => {
        console.log('Error fetching projects:');
        console.log(this.error);
        this.error = 'Failed to load projects.';
        this.toastr.error('Failed to load projects');
        this.loading = false;
      }
    });

  }

  onCustomerSearchChange(): void {
    this.applyFilterAndSort();
  }

  setSort(field: ProjectSortField): void {
    const next = toggleListSort(this.sortField, field, this.sortAsc);
    this.sortField = next.sortField as ProjectSortField;
    this.sortAsc = next.sortAsc;
    this.applyFilterAndSort();
  }

  sortIndicator(field: ProjectSortField): string {
    return listSortIndicator(this.sortField, field, this.sortAsc);
  }

  private applyFilterAndSort(): void {
    let rows = [...this.projectList];
    const customerQ = this.customerSearch.trim().toLowerCase();
    if (customerQ) {
      rows = rows.filter((p) =>
        rowMatchesSearch(p, customerQ, ['CustomerShortCode', 'ProjectShortCode', 'ProjectName']),
      );
    }
    rows = sortRows(rows, this.sortField, this.sortAsc, this.projectNumericSortFields);
    this.displayedProjectList = rows;
  }

  Manage_slides(ProjectShortCode: string){
        console.log("entered manage sides");
       let shortcode = ProjectShortCode;
    this.router.navigate(['/creator/project-slides',shortcode ]);
    }

  loadLanguages(): void {
    this.languageSvc.getLanguages().subscribe({
      next: (rows) => {
        this.languages = Array.isArray(rows) ? rows : [];
      },
      error: () => {
        this.languages = [];
      },
    });
  }

  exportLanguageFor(projectCode: string): string {
    return this.projectCurrentLangSvc.get(projectCode);
  }

  onExportLanguageChange(projectCode: string, languageShortCode: string): void {
    this.projectCurrentLangSvc.set(projectCode, languageShortCode);
  }

  exportLanguageLabel(projectCode: string): string {
    return this.projectCurrentLangSvc.labelFor(this.exportLanguageFor(projectCode), this.languages);
  }

  private safeTransferZipBase(projectCode: string): string {
    return String(projectCode).replace(/[^a-zA-Z0-9._-]+/g, '_');
  }

  createTransferFiles(projectCode: string): void {
    if (this.transferExportingCode) {
      return;
    }
    this.transferExportingCode = projectCode;
    this.transferExportingMessage = 'Creating transfer file…';
    void firstValueFrom(
      this.projectSlidesService.createTransferFiles(projectCode, this.exportLanguageFor(projectCode)).pipe(
        finalize(() => {
          this.transferExportingCode = null;
          this.transferExportingMessage = '';
        }),
      ),
    )
      .then((data: any) => {
        this.toastr.success(data?.message || 'Transfer files created.');
      })
      .catch((err) => {
        this.toastr.error(
          extractApiErrorMessage(err, 'Failed to create transfer files. Processing may not have completed.'),
        );
      });
  }

  downloadTransferZip(projectCode: string): void {
    if (this.transferExportingCode) {
      return;
    }
    this.transferExportingCode = projectCode;
    this.transferExportingMessage = 'Creating ZIP…';
    void firstValueFrom(
      this.projectSlidesService.downloadTransferPackage(projectCode).pipe(
        finalize(() => {
          this.transferExportingCode = null;
          this.transferExportingMessage = '';
        }),
      ),
    )
      .then((blob: Blob) => {
        if (!blob || blob.size === 0) {
          this.toastr.error('Transfer package was empty. Create transfer files first, then download again.');
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.safeTransferZipBase(projectCode)}_package.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Transfer package downloaded.');
      })
      .catch((err) => {
        this.toastr.error(
          extractApiErrorMessage(
            err,
            'Failed to download ZIP. Create the transfer file first, or try again if the request timed out.',
          ),
        );
      });
  }

  onProjectLifecycleChange(project: any, ev: Event) {
    const v = (ev.target as HTMLSelectElement).value;
    if (!v || !project?.ProjectShortCode) {
      return;
    }
    this.projectService.changeProjectLifecycle(project.ProjectShortCode, v).subscribe({
      next: () => {
        this.toastr.success('Project status updated.');
        this.loadProjects();
      },
      error: (err) =>
        this.toastr.error(extractApiErrorMessage(err, 'Failed to update project status.')),
    });
  }

}